import AppConfigurationService from '../app-configuration-service/app-configuration-service';
import { consentService } from '../consent-service/consent-service';
import { cafexService } from '../cafex-service/cafex-service';
import { getFmaState } from '@services/authentication-service/utils/fma-state';
import { isSignInAndSecurityFeature } from '@services/authentication-service/utils/is-webview';
import {
    EU_CHAT_BOT_URLS,
    SUPPRESS_US_COOKIE_BANNER_COUNTRIES,
} from '@/constants';

class ScriptService {
    public loadOneLinkTranslationScript() {
        if (document.getElementById('oneLinkTranslationScript')) {
            return;
        }

        const script = document.createElement('script');
        script.id = 'oneLinkTranslationScript';
        script.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('src', 'https://www.onelink-edge.com/moxie.min.js');
        script.setAttribute('data-oljs', 'PE065-56C9-59DE-5734');
        document.head.appendChild(script);
    }

    public loadFMA() {
        const appConfiguration =
            new AppConfigurationService().getAppConfiguration();
        // The signInAndSecurity feature signal survives inside the OAuth state
        // payload, so it stays mobile across the FMA redirect that drops the
        // /signin-credentials path.
        const appId =
            isSignInAndSecurityFeature(globalThis.location?.search) &&
            appConfiguration.mobileAppId
                ? appConfiguration.mobileAppId
                : appConfiguration.appId;

        // GA simplified brand roots (/ford/{locale}/, /lincoln/{locale}/)
        // must use the bare origin as the registered redirect URI. The path
        // travels in FMA state instead. For Akamai/market roots
        // (/myaccount/, etc.) the full origin+root is registered.
        const isBrandRoot =
            appConfiguration.root.startsWith('/ford/') ||
            appConfiguration.root.startsWith('/lincoln/');
        const origin = isBrandRoot
            ? `https://${window.location.hostname}`
            : `https://${window.location.hostname}${appConfiguration.root}`;
        const fmaTag = document.createElement('script');
        const queryParams = new URLSearchParams(window.location.search);
        const locale = queryParams.get('locale');
        const localeRegex = /^[a-zA-Z]{2}-[a-zA-Z]{2}$/;
        const isValidLocale = locale && localeRegex.test(locale);
        fmaTag.src = appConfiguration.fmaUrl;
        fmaTag.setAttribute('data-fma-script', '');
        const clientId = appConfiguration.clientId
            ? appConfiguration.clientId
            : process.env.REACT_APP_IBM_CID;
        fmaTag.setAttribute('data-client-id', clientId || '');
        fmaTag.dataset.basePath = isBrandRoot ? '' : appConfiguration.root;
        fmaTag.dataset.basePathRedirection = isBrandRoot
            ? 'false'
            : (
                  appConfiguration.brand &&
                  appConfiguration.languageRegionCode &&
                  appConfiguration.root ===
                      `/${appConfiguration.brand}/${appConfiguration.languageRegionCode}/`
              ).toString();
        fmaTag.setAttribute('data-enable-guest-guid', 'false');
        fmaTag.dataset.appId = appId;
        fmaTag.setAttribute('data-property-key', 'brand_ford');
        fmaTag.setAttribute('data-region', appConfiguration.fmaRegion);
        fmaTag.setAttribute('data-fs-url', appConfiguration.fmaFsUrl);
        fmaTag.setAttribute(
            'data-state',
            getFmaState(
                window.location.pathname,
                window.location.search,
                appConfiguration.root
            )
        );
        fmaTag.dataset.redirectUrl = origin;
        fmaTag.setAttribute('data-logout-url', origin);
        fmaTag.setAttribute('data-origin-url', origin);
        if (
            isValidLocale &&
            SUPPRESS_US_COOKIE_BANNER_COUNTRIES.includes(locale)
        ) {
            console.log('Suppressing cookie banner for locale: ', locale);
            fmaTag.setAttribute('data-lang', locale.split('-').join('_'));
        } else {
            fmaTag.setAttribute(
                'data-lang',
                appConfiguration.languageRegionCode === 'no-no'
                    ? 'nb_no'
                    : appConfiguration.languageRegionCode.split('-').join('_')
            );
        }
        document.body.appendChild(fmaTag);
    }

    public unloadFMA() {
        const fmaTag = document.body.querySelector('script[data-fma-script]');
        if (fmaTag && document.body.contains(fmaTag)) {
            document.body.removeChild(fmaTag);
        }
    }

    public loadOneTrustCookieBanner() {
        const appConfiguration =
            new AppConfigurationService().getAppConfiguration();
        const oneTrustTag = document.createElement('script');
        oneTrustTag.setAttribute(
            'src',
            'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
        );
        oneTrustTag.setAttribute('type', 'text/javascript');
        oneTrustTag.setAttribute('charset', 'UTF-8');
        oneTrustTag.setAttribute('data-document-language', 'true');
        oneTrustTag.async = false;
        oneTrustTag.defer = false;

        const domainScriptMap: Record<string, Record<string, string>> = {
            ford: {
                us: '474f6f1e-8e77-40c9-97fd-ab58b6e08de0',
                ca: '36e4189f-d97e-4c35-9018-e1755788c7dc',
            },
            lincoln: {
                us: 'e68b490a-bbfa-4cf0-a492-de3c092da1df',
                ca: 'a68d239d-f6d3-439b-b094-f643bf6530b7',
            },
        };

        const guid =
            domainScriptMap[appConfiguration.brand]?.[
                appConfiguration.countryCode
            ] ??
            domainScriptMap[appConfiguration.brand]?.['us'] ??
            '474f6f1e-8e77-40c9-97fd-ab58b6e08de0';

        const finalGuid =
            process.env.REACT_APP_ACTIVE_PROFILE &&
            process.env.REACT_APP_ACTIVE_PROFILE !== 'prod'
                ? guid + '-test'
                : guid;

        oneTrustTag.setAttribute('data-domain-script', finalGuid);
        document.getElementsByTagName('head')[0].appendChild(oneTrustTag);
    }

    public loadEuHelpRouterScript() {
        const helpRouterScript = document.createElement('script');
        const helpRouterLink = document.createElement('link');
        const helpRouterFonts = document.createElement('link');
        const helpRouterIcons = document.createElement('link');
        const helpRouterVariables = document.createElement('link');
        helpRouterFonts.setAttribute('rel', 'stylesheet');
        helpRouterFonts.setAttribute(
            'href',
            EU_CHAT_BOT_URLS.FORD_ANTENNA_SWAP_CSS
        );
        helpRouterIcons.setAttribute('rel', 'stylesheet');
        helpRouterIcons.setAttribute('href', EU_CHAT_BOT_URLS.FORD_ICONS_CSS);
        helpRouterVariables.setAttribute('rel', 'stylesheet');
        helpRouterVariables.setAttribute(
            'href',
            EU_CHAT_BOT_URLS.FORD_VARIABLES_CSS
        );

        document.body.appendChild(helpRouterFonts);
        document.body.appendChild(helpRouterIcons);
        document.body.appendChild(helpRouterVariables);

        if (process.env.REACT_APP_ACTIVE_PROFILE === 'prod') {
            helpRouterScript.setAttribute('type', 'text/javascript');
            helpRouterScript.setAttribute(
                'src',
                EU_CHAT_BOT_URLS.PROD_CHAT_BOT_BUNDLE_JS
            );
            helpRouterLink.setAttribute('rel', 'stylesheet');
            helpRouterLink.setAttribute('type', 'text/css');
            helpRouterLink.setAttribute(
                'href',
                EU_CHAT_BOT_URLS.PROD_CHAT_BOT_BUNDLE_CSS
            );
            document.body.appendChild(helpRouterScript);
            document.body.appendChild(helpRouterLink);
        } else {
            helpRouterScript.setAttribute('type', 'text/javascript');
            helpRouterScript.setAttribute(
                'src',
                EU_CHAT_BOT_URLS.PERF_CHAT_BOT_BUNDLE_JS
            );
            helpRouterLink.setAttribute('rel', 'stylesheet');
            helpRouterLink.setAttribute('type', 'text/css');
            helpRouterLink.setAttribute(
                'href',
                EU_CHAT_BOT_URLS.PERF_CHAT_BOT_BUNDLE_CSS
            );
            document.body.appendChild(helpRouterScript);
            document.body.appendChild(helpRouterLink);
        }
    }

    public loadAnalyticsScript(loadAsTextPlain = true) {
        const appConfiguration =
            new AppConfigurationService().getAppConfiguration();
        if (appConfiguration.analyticsEmbedScriptUrl) {
            const analyticsTag = document.createElement('script');
            analyticsTag.src = appConfiguration.analyticsEmbedScriptUrl;
            analyticsTag.setAttribute('class', 'optanon-category-2-4');
            analyticsTag.setAttribute('type', 'text/plain');
            document.body.appendChild(analyticsTag);
        }
    }

    public loadDynatraceTags(loadAsTextPlain = true) {
        const dynatraceTag = document.createElement('script');
        dynatraceTag.src =
            'https://js-cdn.dynatrace.com/jstag/19223e8e12b/bf87003hfh/f910f03e5c1f3b21_complete.js';
        dynatraceTag.setAttribute('class', 'optanon-category-2');
        dynatraceTag.setAttribute('type', 'text/plain');
        dynatraceTag.setAttribute('crossorigin', 'anonymous');
        document.body.appendChild(dynatraceTag);
    }

    public loadWalletScript = (callback) => {
        const appConfiguration =
            new AppConfigurationService().getAppConfiguration();
        const existingScript = document.getElementById('walletScript');

        if (!existingScript) {
            const walletTag = document.createElement('script');
            walletTag.id = 'walletScript';
            walletTag.src = appConfiguration.walletUrl;
            walletTag.async = true;
            walletTag.defer = true;
            document.head.appendChild(walletTag);

            walletTag.onload = () => {
                if (callback) callback();
            };
        }

        if (existingScript && callback) callback();
    };

    public loadStripeScript = (): void => {
        const stripeScriptTag = document.createElement('script');
        stripeScriptTag.id = 'stripeScript';
        stripeScriptTag.src = 'https://js.stripe.com/v3/';
        stripeScriptTag.async = true;
        stripeScriptTag.defer = true;
        document.head.appendChild(stripeScriptTag);
    };

    public loadConnectedServicesScript() {
        const appConfiguration =
            new AppConfigurationService().getAppConfiguration();
        const subscriptionTag = document.createElement('script');
        subscriptionTag.id = 'subscriptionScript';

        if (appConfiguration.subscriptionUrl) {
            subscriptionTag.src = appConfiguration.subscriptionUrl;
            subscriptionTag.async = true;
            subscriptionTag.defer = true;
            document.head.appendChild(subscriptionTag);
        }
    }

    public loadCafexScript(removeFlag?: boolean) {
        const cafexScriptPath = cafexService.getCafexScriptPath();
        const isWebview =
            typeof window !== 'undefined'
                ? (window as any)?.SERVER_CONTEXT?.webview === 'true'
                : false;
        if (cafexScriptPath && !isWebview) {
            const cafexTag = document.createElement('script');
            cafexTag.setAttribute('src', cafexScriptPath);
            cafexTag.setAttribute('id', 'cafexscriptid');
            if (consentService.nonEssentialScriptsAllowed() && !removeFlag) {
                cafexTag.setAttribute('class', 'optanon-category-2-4');
                document.body.appendChild(cafexTag);
            } else if (removeFlag) {
                const cafexEle = document.getElementById('cafexscriptid');
                cafexEle && document.body.removeChild(cafexEle);
            }
        }
    }

    public async loadBingMapsScript(callback: () => any, id?: string) {
        const appConfig = new AppConfigurationService().getAppConfiguration();

        if (appConfig.brand === 'ford' || appConfig.brand === 'lincoln') {
            const bingMapScript = document.createElement('script');
            const src =
                'https://www.bing.com/api/maps/mapcontrol?key=Ag5iZ-Ijk6WI40aWrWa-gwOzCJPB0jYdQPXKFq_pB94h0mQ7JgzkrvvWtvxXYYh_';

            bingMapScript.setAttribute('type', 'text/javascript');
            bingMapScript.setAttribute('class', 'optanon-category-2-4');
            bingMapScript.src = src;
            if (id) {
                bingMapScript.id = id;
            }
            bingMapScript.async = true;
            bingMapScript.defer = true;

            document.head.appendChild(bingMapScript);
            bingMapScript.onload = () => {
                if (callback) {
                    callback();
                }
            };
        }
    }

    public async loadGoogleMapsScript(callback, apiKey: string) {
        const appConfig = new AppConfigurationService().getAppConfiguration();

        if (appConfig.brand === 'ford' || appConfig.brand === 'lincoln') {
            const googleMapScript = document.createElement('script');
            const src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;

            googleMapScript.setAttribute('type', 'text/javascript');
            googleMapScript.setAttribute('class', 'optanon-category-2-4');
            googleMapScript.src = src;
            googleMapScript.id = 'google-map-script';
            googleMapScript.async = true;
            googleMapScript.defer = true;
            document.head.appendChild(googleMapScript);
            googleMapScript.onload = () => {
                if (callback) {
                    callback();
                }
            };
        }
    }

    public loadRecaptchaScript() {
        const recaptchaTag = document.createElement('script');
        recaptchaTag.setAttribute('type', 'text/javascript');
        recaptchaTag.setAttribute('class', 'optanon-category-2-4');
        recaptchaTag.id = 'recaptchaScript';
        recaptchaTag.src = 'https://www.google.com/recaptcha/api.js';
        document.head.appendChild(recaptchaTag);
    }
}

export const scriptService = new ScriptService();
export default ScriptService;
